import React, { useEffect, useMemo, useState } from 'react';

import { capitalize } from 'lodash';

import { NavLinkButton, Button, Table as BootstrapTable } from '@shared/components/bootstrap';
import { Timestamp, Retry } from '@shared/components/helpers';
import { Pagination } from '@shared/components/pagination/pagination';
import {
  dispatchDocumentEvent,
  WAREHOUSE_TASK_COUNT_UPDATED_EVENT,
  WarehouseTaskCountUpdatedEventDetail,
} from '@shared/hooks/use_document_event_listener';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Refresh } from '@admin/components/refresh';
import { Spinner } from '@admin/components/spinner';
import { currentUser } from '@admin/config/current_user';
import { accountURL, warehouseTaskURL, newWarehouseTaskURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { WAREHOUSE_OPS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  useWarehouseTasksQuery,
  useWarehouseTasksUnreadQuery,
  useWarehousesSelectorQuery,
  useWarehouseTaskMarkViewedMutation,
  WarehouseTask__ViewMode,
  useWarehouseTaskMarkAllViewedMutation,
  useWarehouseTaskLocationMarkViewedMutation,
  useWarehouseTaskLocationMarkAllViewedMutation,
  WarehouseTasksUnreadQuery,
  WarehouseTasksQuery,
} from '@admin/schema';

import { STATE_LABELS, stateClass } from './constants';
import { Filters, FiltersState, DEFAULT_FILTERS } from './filters';

type CreatedUnreadTask = WarehouseTasksUnreadQuery['warehouseTasksCreatedUnread'][number];
type LocationUnreadActivity = WarehouseTasksUnreadQuery['warehouseTasksLocationUnread'][number];
type ListTaskResult = WarehouseTasksQuery['warehouseTasks']['results'][number];

const visibleSubTasks = (task: ListTaskResult, warehouseID?: string, managedWarehouseIDs?: Set<string>) => {
  if (warehouseID) {
    return task.subTasks.filter((st) => st.warehouse.id === warehouseID);
  }
  if (managedWarehouseIDs?.size) {
    return task.subTasks.filter((st) => managedWarehouseIDs.has(st.warehouse.id));
  }
  return task.subTasks;
};

const UnreadTable: React.FC<{
  rows: Array<{
    key: string;
    taskId: string;
    accountName: string;
    accountId: string;
    state: string;
    update: string;
    updatedAt: string;
    onMarkRead: () => void;
  }>;
  loading: boolean;
}> = ({ rows, loading }) => (
  <BootstrapTable responsive>
    <thead>
      <tr>
        <th>Task</th>
        <th>Account</th>
        <th>Status</th>
        <th>Update</th>
        <th>Updated</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr
          key={row.key}
          onClick={() => {
            window.location.href = warehouseTaskURL({ id: row.taskId });
          }}
          style={{ cursor: 'pointer', backgroundColor: '#fdf6ec' }}
        >
          <td>
            <a
              href={warehouseTaskURL({ id: row.taskId })}
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#17a2b8', fontWeight: 600, textDecoration: 'underline' }}
            >
              #{row.taskId}
            </a>
          </td>
          <td>
            <a
              href={accountURL({ id: row.accountId })}
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#17a2b8', textDecoration: 'underline' }}
            >
              {row.accountName}
            </a>
          </td>
          <td>
            <span className={`label ${stateClass(row.state)}`}>{STATE_LABELS[row.state] || capitalize(row.state)}</span>
          </td>
          <td>{row.update}</td>
          <td>
            <Timestamp value={row.updatedAt} format={Timestamp.Format.DateTime} />
          </td>
          <td>
            <button
              className="btn btn-xs btn-default"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                row.onMarkRead();
              }}
              disabled={loading}
            >
              Mark as read
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </BootstrapTable>
);

const UnreadUpdatesPanel: React.FC<{
  createdTasks: CreatedUnreadTask[];
  locationActivities: LocationUnreadActivity[];
  onMarkCreatedRead: (id: string) => Promise<void>;
  onMarkAllCreatedRead: () => Promise<void>;
  onMarkLocationRead: (activityId: string) => Promise<void>;
  onMarkAllLocationRead: () => Promise<void>;
  onMarkAllRead: () => Promise<void>;
  loading: boolean;
  mutationError: string | null;
}> = ({
  createdTasks,
  locationActivities,
  onMarkCreatedRead,
  onMarkAllCreatedRead,
  onMarkLocationRead,
  onMarkAllLocationRead,
  onMarkAllRead,
  loading,
  mutationError,
}) => {
  const totalUnread = createdTasks.length + locationActivities.length;
  if (totalUnread === 0) return null;

  const hasCreatedUnread = createdTasks.length > 0;
  const hasLocationUnread = locationActivities.length > 0;
  const showSplitSections = hasCreatedUnread && hasLocationUnread;

  const createdRows = createdTasks.map((task) => ({
    key: `created-${task.id}`,
    taskId: task.id,
    accountId: task.account.id,
    accountName: task.account.name,
    state: task.state,
    update: `Your task · ${task.unreadActivity}`,
    updatedAt: task.updatedAt,
    onMarkRead: () => onMarkCreatedRead(task.id),
  }));

  const locationRows = locationActivities.map((activity) => ({
    key: `location-${activity.id}`,
    taskId: activity.warehouseTask.id,
    accountId: activity.warehouseTask.account.id,
    accountName: activity.warehouseTask.account.name,
    state: activity.warehouseTask.state,
    update: activity.label,
    updatedAt: activity.occurredAt,
    onMarkRead: () => onMarkLocationRead(activity.id),
  }));

  const allRows = [...createdRows, ...locationRows];

  return (
    <Panel>
      <Panel.Header>
        <Panel.Control>
          {showSplitSections ? (
            <>
              <Button
                kind="default"
                size="sm"
                onClick={() => onMarkAllCreatedRead()}
                disabled={loading || !hasCreatedUnread}
              >
                Mark created updates read
              </Button>
              <Button
                kind="default"
                size="sm"
                onClick={() => onMarkAllLocationRead()}
                disabled={loading || !hasLocationUnread}
                style={{ marginLeft: 8 }}
              >
                Mark location updates read
              </Button>
            </>
          ) : (
            <Button kind="default" size="sm" onClick={() => onMarkAllRead()} disabled={loading}>
              Mark all updates as read
            </Button>
          )}
        </Panel.Control>
        <Panel.Title>
          Unread Updates
          <span className="badge badge-warning" style={{ marginLeft: 8 }}>
            {totalUnread}
          </span>
        </Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {mutationError && <div className="alert alert-danger">{mutationError}</div>}
        {showSplitSections ? (
          <>
            {hasCreatedUnread && (
              <div style={{ marginBottom: 24 }}>
                <h5 style={{ marginTop: 0 }}>Updates on tasks you created</h5>
                <UnreadTable rows={createdRows} loading={loading} />
              </div>
            )}
            {hasLocationUnread && (
              <div>
                <h5 style={{ marginTop: 0 }}>Updates at your warehouse(s)</h5>
                <UnreadTable rows={locationRows} loading={loading} />
              </div>
            )}
          </>
        ) : (
          <UnreadTable rows={allRows} loading={loading} />
        )}
      </Panel.Body>
    </Panel>
  );
};

const WarehouseTasksListContent: React.FC = () => {
  useTitle('Warehouse Tasks');
  const canCreateTasks = usePermission(WAREHOUSE_OPS.resource, WAREHOUSE_OPS.actions.create);
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  const [unreadMutationError, setUnreadMutationError] = useState<string | null>(null);
  const atMyWarehouses = filters.viewMode === WarehouseTask__ViewMode.AtMyWarehouses;
  const regionIDs = currentUser.region_ids?.map(String);

  const { data: managedWarehousesData } = useWarehousesSelectorQuery({
    client,
    variables: { filters: regionIDs?.length ? { regionIDs } : undefined },
    skip: !atMyWarehouses || !regionIDs?.length,
  });

  const managedWarehouseIDs = useMemo(() => {
    if (!atMyWarehouses || filters.warehouseID || !managedWarehousesData?.warehouses) {
      return undefined;
    }
    return new Set(managedWarehousesData.warehouses.map((warehouse) => warehouse.id));
  }, [atMyWarehouses, filters.warehouseID, managedWarehousesData]);

  const {
    data: unreadData,
    loading: unreadLoading,
    error: unreadError,
    refetch: refetchUnread,
  } = useWarehouseTasksUnreadQuery({
    client,
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading, error, refetch } = useWarehouseTasksQuery({
    client,
    variables: {
      filters: {
        accountID: filters.accountID,
        dueDateFrom: filters.dueDateFrom,
        dueDateTo: filters.dueDateTo,
        page: filters.page,
        search: filters.search,
        startDate: filters.startDate,
        endDate: filters.endDate,
        state: filters.state,
        viewMode: filters.viewMode,
        warehouseID: filters.warehouseID,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [markViewed, { loading: markingViewed }] = useWarehouseTaskMarkViewedMutation({ client });
  const [markAllViewed, { loading: markingAllViewed }] = useWarehouseTaskMarkAllViewedMutation({ client });
  const [markLocationViewed, { loading: markingLocationViewed }] = useWarehouseTaskLocationMarkViewedMutation({
    client,
  });
  const [markAllLocationViewed, { loading: markingAllLocationViewed }] = useWarehouseTaskLocationMarkAllViewedMutation({
    client,
  });
  const mutating = markingViewed || markingAllViewed || markingLocationViewed || markingAllLocationViewed;

  const unreadCount = unreadData?.warehouseTaskUnreadCount;

  useEffect(() => {
    if (unreadCount !== undefined) {
      dispatchDocumentEvent<WarehouseTaskCountUpdatedEventDetail>(WAREHOUSE_TASK_COUNT_UPDATED_EVENT, {
        count: unreadCount,
      });
    }
  }, [unreadCount]);

  const refreshUnread = async () => {
    await refetchUnread();
  };

  const refreshAll = async () => {
    await Promise.all([refetch(), refetchUnread()]);
  };

  const handleMarkAllCreatedRead = async () => {
    setUnreadMutationError(null);
    try {
      const result = await markAllViewed();
      const payload = result.data?.warehouseTaskMarkAllViewed;
      if (payload?.status !== 'OK') {
        setUnreadMutationError(payload?.error || 'Failed to mark created updates as read');
      }
      await refreshUnread();
    } catch (err) {
      setUnreadMutationError(err instanceof Error ? err.message : 'Failed to mark created updates as read');
      await refreshUnread();
    }
  };

  const handleMarkCreatedRead = async (id: string) => {
    setUnreadMutationError(null);
    try {
      const result = await markViewed({ variables: { id } });
      const payload = result.data?.warehouseTaskMarkViewed;
      if (payload?.status !== 'OK') {
        setUnreadMutationError(payload?.error || 'Failed to mark update as read');
      }
      await refreshUnread();
    } catch (err) {
      setUnreadMutationError(err instanceof Error ? err.message : 'Failed to mark update as read');
      await refreshUnread();
    }
  };

  const handleMarkAllLocationRead = async () => {
    setUnreadMutationError(null);
    try {
      const result = await markAllLocationViewed();
      const payload = result.data?.warehouseTaskLocationMarkAllViewed;
      if (payload?.status !== 'OK') {
        setUnreadMutationError(payload?.error || 'Failed to mark location updates as read');
      }
      await refreshUnread();
    } catch (err) {
      setUnreadMutationError(err instanceof Error ? err.message : 'Failed to mark location updates as read');
      await refreshUnread();
    }
  };

  const handleMarkAllRead = async () => {
    setUnreadMutationError(null);
    try {
      const createdCount = unreadData?.warehouseTasksCreatedUnread.length ?? 0;
      const locationCount = unreadData?.warehouseTasksLocationUnread.length ?? 0;

      if (createdCount > 0) {
        const result = await markAllViewed();
        const payload = result.data?.warehouseTaskMarkAllViewed;
        if (payload?.status !== 'OK') {
          setUnreadMutationError(payload?.error || 'Failed to mark updates as read');
          await refreshUnread();
          return;
        }
      }

      if (locationCount > 0) {
        const result = await markAllLocationViewed();
        const payload = result.data?.warehouseTaskLocationMarkAllViewed;
        if (payload?.status !== 'OK') {
          setUnreadMutationError(payload?.error || 'Failed to mark updates as read');
        }
      }

      await refreshUnread();
    } catch (err) {
      setUnreadMutationError(err instanceof Error ? err.message : 'Failed to mark updates as read');
      await refreshUnread();
    }
  };

  const handleMarkLocationRead = async (activityId: string) => {
    setUnreadMutationError(null);
    try {
      const result = await markLocationViewed({ variables: { activityID: activityId } });
      const payload = result.data?.warehouseTaskLocationMarkViewed;
      if (payload?.status !== 'OK') {
        setUnreadMutationError(payload?.error || 'Failed to mark location update as read');
      }
      await refreshUnread();
    } catch (err) {
      setUnreadMutationError(err instanceof Error ? err.message : 'Failed to mark location update as read');
      await refreshUnread();
    }
  };

  if (error) return <Retry error={error} refetch={refetch} />;

  return (
    <div>
      {unreadError ? (
        <Retry error={unreadError} refetch={refetchUnread} />
      ) : (
        unreadData && (
          <UnreadUpdatesPanel
            createdTasks={unreadData.warehouseTasksCreatedUnread}
            locationActivities={unreadData.warehouseTasksLocationUnread}
            onMarkCreatedRead={handleMarkCreatedRead}
            onMarkAllCreatedRead={handleMarkAllCreatedRead}
            onMarkLocationRead={handleMarkLocationRead}
            onMarkAllLocationRead={handleMarkAllLocationRead}
            onMarkAllRead={handleMarkAllRead}
            loading={mutating || unreadLoading}
            mutationError={unreadMutationError}
          />
        )
      )}

      <Panel>
        <Panel.Header>
          {canCreateTasks && (
            <Panel.Control>
              <NavLinkButton kind="primary" to={newWarehouseTaskURL()}>
                Create Task
              </NavLinkButton>
            </Panel.Control>
          )}
          <Panel.Title>Warehouse Tasks</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <div style={{ marginBottom: 16 }}>
            <Refresh refetch={refreshAll} intervalSeconds={15} />
          </div>
          <Filters filters={filters} onChange={setFilters} />
          {loading && !data ? (
            <Spinner />
          ) : (
            <BootstrapTable striped responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Account</th>
                  <th>Status</th>
                  <th>Warehouses</th>
                  <th>Claimed By</th>
                  <th>Created By</th>
                  <th>Due Date</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data?.warehouseTasks.results.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <a
                        href={warehouseTaskURL({ id: task.id })}
                        style={{ color: '#17a2b8', fontWeight: 600, textDecoration: 'underline' }}
                      >
                        #{task.id}
                      </a>
                    </td>
                    <td>
                      <a
                        href={accountURL({ id: task.account.id })}
                        style={{ color: '#17a2b8', textDecoration: 'underline' }}
                      >
                        {task.account.name}
                      </a>
                    </td>
                    <td>
                      <span className={`label ${stateClass(task.state)}`}>
                        {STATE_LABELS[task.state] || capitalize(task.state)}
                      </span>
                    </td>
                    <td>
                      {visibleSubTasks(task, filters.warehouseID, managedWarehouseIDs).map((st) => (
                        <div key={st.id}>{st.warehouse.name}</div>
                      ))}
                    </td>
                    <td>
                      {visibleSubTasks(task, filters.warehouseID, managedWarehouseIDs)
                        .flatMap((st) => st.assignees.map((a) => a.name))
                        .join(', ') || '-'}
                    </td>
                    <td>{task.creator.name}</td>
                    <td>{task.dueDate && <Timestamp value={task.dueDate} format={Timestamp.Format.Date} />}</td>
                    <td>
                      <Timestamp value={task.createdAt} format={Timestamp.Format.Date} />
                    </td>
                    <td>
                      <NavLinkButton kind="primary" size="sm" to={warehouseTaskURL({ id: task.id })}>
                        View
                      </NavLinkButton>
                    </td>
                  </tr>
                ))}
                {data?.warehouseTasks.results.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted">
                      No warehouse tasks found
                    </td>
                  </tr>
                )}
              </tbody>
            </BootstrapTable>
          )}
        </Panel.Body>
        {data && (
          <Panel.Footer>
            <Pagination
              pagination={data.warehouseTasks.pagination}
              onPage={(page) => setFilters((prev) => ({ ...prev, page }))}
            />
          </Panel.Footer>
        )}
      </Panel>
    </div>
  );
};

export const WarehouseTasksList: React.FC = () => (
  <Permission resource={WAREHOUSE_OPS.resource} action={WAREHOUSE_OPS.actions.read}>
    <WarehouseTasksListContent />
  </Permission>
);
